import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import type { DailyStaffingDayView } from '../../../../services/LaborManagementService/types/laborManagementTypes';
import DailyInputsSection from './DailyInputsSection';
import { formatDayDate } from './dayDate';

const fmtNum = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });
// Non-negative, at most two decimal places, refused as typed (mirrors DailyInputsSection's guard).
const isEnteredActualValid = (value: string) => /^\d*\.?\d{0,2}$/.test(value);
const DEPARTMENT_COLUMN_WIDTH = 150;
const CALC_COLUMN_WIDTH = 58;
const ACTUAL_COLUMN_WIDTH = 76;
const DAY_PAIR_WIDTH = CALC_COLUMN_WIDTH + ACTUAL_COLUMN_WIDTH;
const DAY_PAIR_TEMPLATE = `${CALC_COLUMN_WIDTH}px ${ACTUAL_COLUMN_WIDTH}px`;

const buildDayDepartmentKey = (dayOfWeek: number, departmentName: string) =>
  `${dayOfWeek}|${departmentName}`;

const isSubduedDay = (day: DailyStaffingDayView) => day.scheduledCases === 0 && day.rooms === 0;


const DEPT_DISPLAY: Record<string, string> = {
  PreOpRN: 'Pre-Op RN',
  PacuRN: 'PACU RN',
  ORCirculatingRN: 'OR Circulating RN',
  ORScrubTech: 'OR Scrub Tech',
  XrayTech: 'X-ray Tech',
  FirstAssist: 'First Assist',
  SterileProcessingTech: 'Sterile Processing Tech',
  PatientCareTech: 'Patient Care Tech',
  ORRunnerFloat: 'OR Runner/Float',
};

interface DailyStaffingGridProps {
  days: DailyStaffingDayView[];
  actualEdits: Record<string, string>;
  onActualChange: (dayOfWeek: number, deptName: string, value: string) => void;
  roomsEdits: Record<number, string>;
  onRoomsChange: (dayOfWeek: number, value: string) => void;
  traysEdits?: Record<number, string>;
  onTraysChange?: (dayOfWeek: number, value: string) => void;
  disabled: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  onRetry?: () => void;
}

const DailyStaffingGrid: React.FC<DailyStaffingGridProps> = ({
  days,
  actualEdits,
  onActualChange,
  roomsEdits,
  onRoomsChange,
  traysEdits = {},
  onTraysChange = () => undefined,
  disabled,
  isLoading = false,
  errorMessage,
  onRetry,
}) => {
  const orderedDays = useMemo(
    () => [...days].sort((a, b) => a.dayOfWeek - b.dayOfWeek),
    [days],
  );

  const departmentNames = useMemo(() => {
    const ordered: string[] = [];
    const seen = new Set<string>();

    for (const day of orderedDays) {
      for (const department of day.departments) {
        if (!seen.has(department.departmentName)) {
          seen.add(department.departmentName);
          ordered.push(department.departmentName);
        }
      }
    }

    return ordered;
  }, [orderedDays]);

  const departmentByDayKey = useMemo(() => {
    const lookup = new Map<string, { calculatedStaff: number; actualStaff: number | null }>();

    for (const day of orderedDays) {
      for (const department of day.departments) {
        lookup.set(
          buildDayDepartmentKey(day.dayOfWeek, department.departmentName),
          {
            calculatedStaff: department.calculatedStaff,
            actualStaff: department.actualStaff,
          },
        );
      }
    }

    return lookup;
  }, [orderedDays]);

  const totalsByDay = useMemo(() => {
    const totals = new Map<number, { calculated: number; actual: number }>();

    for (const day of orderedDays) {
      totals.set(day.dayOfWeek, { calculated: 0, actual: 0 });
    }

    for (const day of orderedDays) {
      for (const departmentName of departmentNames) {
        const department = departmentByDayKey.get(buildDayDepartmentKey(day.dayOfWeek, departmentName));
        const current = totals.get(day.dayOfWeek);
        if (!current) continue;

        const editKey = buildDayDepartmentKey(day.dayOfWeek, departmentName);
        const editValue = actualEdits[editKey];
        const actualValue = editValue === '' || editValue == null
          ? 0
          : parseFloat(editValue) || 0;

        totals.set(day.dayOfWeek, {
          calculated: current.calculated + (department?.calculatedStaff ?? 0),
          actual: current.actual + actualValue,
        });
      }
    }

    return totals;
  }, [orderedDays, departmentNames, actualEdits, departmentByDayKey]);

  const columnTemplate = useMemo(() => {
    if (orderedDays.length === 0) {
      return `${DEPARTMENT_COLUMN_WIDTH}px`;
    }

    return `${DEPARTMENT_COLUMN_WIDTH}px repeat(${orderedDays.length}, ${DAY_PAIR_WIDTH}px)`;
  }, [orderedDays.length]);

  const gridMinWidth = useMemo(
    () => DEPARTMENT_COLUMN_WIDTH + (orderedDays.length * DAY_PAIR_WIDTH),
    [orderedDays.length],
  );

  const loadingDepartmentCount = Math.max(3, departmentNames.length || 0);

  return (
    <Box sx={{ mt: 0.5 }}>
      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
        >
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: gridMinWidth, width: 'max-content', maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: columnTemplate,
              gap: 0.5,
              pb: 0.25,
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8' }}>
              Department
            </Typography>

            {orderedDays.map((day) => {
              const subdued = isSubduedDay(day);
              const isLastDay = day.dayOfWeek === orderedDays[orderedDays.length - 1]?.dayOfWeek;

              return (
                <Box
                  key={`day-header-${day.dayOfWeek}`}
                  data-testid={`day-header-${day.dayOfWeek}`}
                  data-zero-cases={subdued ? 'true' : 'false'}
                  sx={{
                    px: 0.4,
                    py: 0.35,
                    borderRadius: 1,
                    backgroundColor: subdued ? '#f8fafc' : '#eef2ff',
                    textAlign: 'center',
                    borderRight: isLastDay ? 'none' : '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', color: subdued ? '#94a3b8' : '#475569' }}>
                    {day.dayLabel} {formatDayDate(day.date)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.66rem', color: subdued ? '#94a3b8' : '#64748b' }}>
                    Cases: {day.scheduledCases}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: columnTemplate,
              gap: 0.5,
              pb: 0.75,
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#94a3b8' }}>
              
            </Typography>
            {orderedDays.map((day) => {
              const subdued = isSubduedDay(day);
              const isLastDay = day.dayOfWeek === orderedDays[orderedDays.length - 1]?.dayOfWeek;

              return (
                <Box
                  key={`sub-header-${day.dayOfWeek}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: DAY_PAIR_TEMPLATE,
                    columnGap: 0,
                    alignItems: 'center',
                    justifyItems: 'center',
                    borderRight: isLastDay ? 'none' : '1px solid #edf2f7',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, fontSize: '0.66rem', color: subdued ? '#94a3b8' : '#64748b' }}
                  >
                    Calc
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.66rem',
                      color: subdued ? '#94a3b8' : '#64748b',
                      width: '100%',
                      textAlign: 'center',
                      borderLeft: '1px solid #e6ebf2',
                    }}
                  >
                    Actual
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {isLoading && (
            <Box data-testid="daily-staffing-loading" sx={{ py: 1.5 }}>
              {Array.from({ length: loadingDepartmentCount }).map((_, idx) => (
                <Box
                  key={`loading-row-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: columnTemplate,
                    gap: 0.5,
                    py: 0.75,
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <Skeleton variant="text" width="70%" />
                  {orderedDays.map((day) => (
                    <Box
                      key={`loading-cell-${idx}-${day.dayOfWeek}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: DAY_PAIR_TEMPLATE,
                        columnGap: 0,
                        alignItems: 'center',
                        borderRight: day.dayOfWeek === orderedDays[orderedDays.length - 1]?.dayOfWeek ? 'none' : '1px solid #f1f5f9',
                      }}
                    >
                      <Skeleton variant="text" width="90%" sx={{ justifySelf: 'end' }} />
                      <Box sx={{ borderLeft: '1px solid #eef2f7', pl: 0.5 }}>
                        <Skeleton variant="rounded" height={30} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {!isLoading && orderedDays.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No daily staffing data available for this week.
            </Typography>
          )}

          {!isLoading && orderedDays.length > 0 && (
            <DailyInputsSection
              days={orderedDays}
              columnTemplate={columnTemplate}
              dayPairTemplate={DAY_PAIR_TEMPLATE}
              disabled={disabled}
              edits={{ roomsEdits, onRoomsChange, traysEdits, onTraysChange }}
            />
          )}

          {!isLoading && departmentNames.map((departmentName) => (
            <Box
              key={`department-row-${departmentName}`}
              data-testid={`department-row-${departmentName}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: columnTemplate,
                gap: 0.5,
                py: 0.55,
                borderBottom: '1px solid #f1f5f9',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                {DEPT_DISPLAY[departmentName] ?? departmentName}
              </Typography>

              {orderedDays.map((day) => {
                const subdued = isSubduedDay(day);
                const department = departmentByDayKey.get(buildDayDepartmentKey(day.dayOfWeek, departmentName));
                const actualKey = buildDayDepartmentKey(day.dayOfWeek, departmentName);
                const actualValue = actualEdits[actualKey] ?? (department?.actualStaff != null ? String(department.actualStaff) : '');
                const isLastDay = day.dayOfWeek === orderedDays[orderedDays.length - 1]?.dayOfWeek;

                return (
                  <Box
                    key={`department-${departmentName}-day-${day.dayOfWeek}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: DAY_PAIR_TEMPLATE,
                      columnGap: 0,
                      alignItems: 'center',
                      borderRight: isLastDay ? 'none' : '1px solid #f1f5f9',
                      pr: isLastDay ? 0 : 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'right',
                        fontSize: '0.76rem',
                        color: subdued ? '#94a3b8' : '#64748b',
                        backgroundColor: subdued ? '#f8fafc' : 'transparent',
                        borderRadius: 1,
                        px: 0.25,
                        py: 0.25,
                      }}
                    >
                      {fmtNum(department?.calculatedStaff ?? 0)}
                    </Typography>
                    <TextField
                      size="small"
                      type="text"
                      value={actualValue}
                      onChange={(e) => {
                        if (isEnteredActualValid(e.target.value)) onActualChange(day.dayOfWeek, departmentName, e.target.value);
                      }}
                      // Select the whole value on focus so typing overwrites the staff count
                      // instead of appending at the click position.
                      onFocus={(e) => e.target.select()}
                      disabled={disabled}
                      inputProps={{
                        min: 0,
                        step: 0.01,
                        inputMode: 'decimal',
                        'aria-label': `Actual staff for ${DEPT_DISPLAY[departmentName] ?? departmentName} on ${day.dayLabel} ${formatDayDate(day.date)}`,
                      }}
                      sx={{
                        borderLeft: '1px solid #e8edf5',
                        pl: 0.5,
                        backgroundColor: subdued ? '#f8fafc' : 'transparent',
                        borderRadius: 1,
                        '& .MuiInputBase-root': {
                          height: 30,
                        },
                        '& input': {
                          textAlign: 'right',
                          py: 0.25,
                          px: 0.75,
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}

          {!isLoading && orderedDays.length > 0 && departmentNames.length > 0 && (
            <Box
              data-testid="daily-row-total"
              sx={{
                display: 'grid',
                gridTemplateColumns: columnTemplate,
                gap: 0.5,
                py: 0.85,
                alignItems: 'center',
                backgroundColor: '#eef2ff',
                borderTop: '1px solid #cbd5e1',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Total
              </Typography>

              {orderedDays.map((day) => {
                const totals = totalsByDay.get(day.dayOfWeek) ?? { calculated: 0, actual: 0 };
                const subdued = isSubduedDay(day);
                const isLastDay = day.dayOfWeek === orderedDays[orderedDays.length - 1]?.dayOfWeek;

                return (
                  <Box
                    key={`total-day-${day.dayOfWeek}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: DAY_PAIR_TEMPLATE,
                      columnGap: 0,
                      alignItems: 'center',
                      borderRight: isLastDay ? 'none' : '1px solid #dbe3ee',
                      pr: isLastDay ? 0 : 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'right',
                        fontWeight: 700,
                        color: subdued ? '#64748b' : '#1e293b',
                        backgroundColor: subdued ? '#e2e8f0' : 'transparent',
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                      }}
                    >
                      {fmtNum(totals.calculated)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'right',
                        fontWeight: 700,
                        color: subdued ? '#64748b' : '#1e293b',
                        backgroundColor: subdued ? '#e2e8f0' : 'transparent',
                        borderRadius: 1,
                        borderLeft: '1px solid #d5deea',
                        px: 0.5,
                        py: 0.25,
                        pr: 1,
                      }}
                    >
                      {fmtNum(totals.actual)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DailyStaffingGrid;
export { DEPT_DISPLAY, buildDayDepartmentKey };
